// © Microsoft Corporation. All rights reserved.

import { getMuidCookie, headData, getLocalStorage } from "@msnews/core";
import { prerenderedPageAge, prerenderedPageReload } from "./PageLifecycleConstants";

import { PageInvalidationReasons } from "./PageInvalidationReasons";
import { Telemetry } from "@msnews/telemetry-contracts";
import { forceReloadFlagName } from "./PageLifecycleConstants";
import { logger } from "@msnews/diagnostics";
import { refreshPage } from "@msnews/experiences-base";

let muidAtLoad;
let invalidationReason: PageInvalidationReasons;
/** The HTML element which is being used as the overlay element */
let overlayElement;
let overlayTimeout;
let hasRevealed = false;

/**
 * Init Prerender
 */
export function initPrerender() {
    muidAtLoad = getMuidCookie() || "";
    overlayElement = document.createElement("div");
}

/**
 * return true if prerendered page is visible else false0
 */
export function isPrerenderVisible(): boolean {
    if (headData.IsPrerender && document.visibilityState === "visible") {
        return true;
    }

    return false;
}

/**
 * Returns true if the page is prerendered and we have not revealed yet.
 */
export function isPrerenderBeforeReveal(): boolean {
    return headData.IsPrerender && !hasRevealed;
}

/**
 * Test-only function to reset hasRevealed. Will be tree-shaken away in prod mode.
 */
export function testResetHasRevealed() {
    hasRevealed = false;
}

/**
 * Process pageReveal state
 * This method should be hooked just before pageReveal is started.
 * !!!IMPORTANT!!! - Hook this up with milestone pageRevealStartWillFire plugin event.
 */
export function processPageRevealedState(): boolean {
    hasRevealed = true;
    if (document.visibilityState !== "visible") {
        return false;
    }

    // remove overlay with timeout
    removeOverlayWithTimeout();

    if (shouldReloadPrerenderPage()) {
        preparePageForReload(getCurrentPerformanceTimeStamp());
    } else {
        firePageRevealTelemetry(getCurrentPerformanceTimeStamp());
    }

    return true;
}

/**
 * current performance timestamp
 */
export function getCurrentPerformanceTimeStamp(): number {
    return Math.round(performance.timing.navigationStart + performance.now());
}

/**
 * Add the overlay which mocks the background. This is needed when a preloaded page on reveal is forced reloaded (stale content, settings change).
 * For preload, we add an overlay over the content, that we show until we know the content is fresh.
 * If the content is not fresh, we will reload to ensure freshness. The purpose of the overlay is to prevent flickering
 * (showing the old content, then the new) when the revealed data is old.
 */
export function addPrerenderOverlay(): void {
    if (!headData.IsPrerender) {
        return;
    }

    if (!overlayElement) {
        return;
    }

    const body = document.body;
    const style = overlayElement.style;

    style.background = "inherit";
    style.position = "fixed";
    style.bottom = style.left = style.right = style.top = "0";
    style.zIndex = "2147483647";

    if (body) {
        body.appendChild(overlayElement);
        logger.logObjects("pageRevealDelegate:addOverlay: Added overlay");
    } else {
        logger.logObjects("pageRevealDelegate:addOverlay: Document body is not available, failed to add overlay.");
    }
}

/**
 * Remove the overlay
 */
export function removePrerenderOverlay(): void {
    try {
        // clear overlayTimeout if its set.
        overlayTimeout && clearTimeout(overlayTimeout);

        // Remove it safely if it is present in the DOM
        document.body.removeChild(overlayElement);
        logger.logObjects("pageRevealDelegate:removeOverlay: Removed overlay");
    } catch (e) {
        // Suppress the error
    }

    overlayElement = null;
}

/**
 * prepare page for reload
 * @param pageRevealTimeStamp - pageReveal start timestamp
 */
function preparePageForReload(pageRevealTimeStamp: DOMHighResTimeStamp): void {
    // save telemetry in persistent storage to be used after page is reloaded.
    savePrerenderedPageReloadTracking(pageRevealTimeStamp);

    // By specifying the new URL, the helper will use location.replace() method which
    // replaces the current resource with the one at the provided URL.
    // After using replace() the current page will not be saved in session History,
    // meaning the user won't be able to use the back button to navigate to it.
    refreshPage({ newUrl: getReloadUrl() });
}

/**
 * should reload hidden page on reveal
 */
function shouldReloadPrerenderPage(): boolean {
    if (!headData.IsPrerender) {
        return false;
    }

    const flag = getLocalStorage().getItem(forceReloadFlagName);

    if (flag) {

        // remove after reading
        getLocalStorage().removeItem(forceReloadFlagName);

        let flagData;

        try {
            flagData = JSON.parse(flag);
            logger.logObjects("pageRevealDelegate:isForceReloadRequested, time: " + (new Date(flagData.time)).toTimeString());
        } catch (e) {
            logger.logObjects("pageRevealDelegate:isForceReloadRequested: JSON parsing failed for fpr data");
        }

        if (flagData) {
            // We should only reload the page if reload request was made after this page navigation started.
            // if the request was made before the page navigation, preloaded page must already have the latest settings.
            // Therefore no need to reload at reveal.

            const time = flagData.time;
            invalidationReason = flagData.data;

            if (!isNaN(time) && (time >  performance.timing.navigationStart)) {
                return true;
            }
        }
    }

    return hasMuidChanged();
}

/**
 * Has muid changed
 */
function hasMuidChanged(): boolean {
    const muid = getMuidCookie()  || "";

    if (muidAtLoad !== muid) {
        invalidationReason = PageInvalidationReasons.MuidChange;
        return true;
    }

    return false;
}

/**
 * Fire Telemetry for prerendered tab reload.
 */
function trackPrerenderedPageReloadTelemetry(): void {
    if (!headData.IsPrerender && document.visibilityState === "visible") {
        const prerenderedPageReloadData = getLocalStorage().getItem(prerenderedPageReload);
        const prerenderedPageAgeData = getLocalStorage().getItem(prerenderedPageAge);

        if (prerenderedPageReloadData) {
            Telemetry.addOrUpdateCustomProperty(prerenderedPageReload, prerenderedPageReloadData);
            getLocalStorage().removeItem(prerenderedPageReload);
        }

        if (prerenderedPageAgeData) {
            Telemetry.addOrUpdateCustomProperty(prerenderedPageAge, prerenderedPageAgeData);
            getLocalStorage().removeItem(prerenderedPageAge);
        }
    }
}

/**
 * Save Telemetry for prerendered tab reload.
 */
function savePrerenderedPageReloadTracking(pageRevealTimeStamp: DOMHighResTimeStamp): void {
    if (headData.IsPrerender && invalidationReason) {
        getLocalStorage().setItem(prerenderedPageReload, invalidationReason + "");
        getLocalStorage().setItem(prerenderedPageAge, (pageRevealTimeStamp - performance.timing.navigationStart).toString());
    }
}

/**
 * Fire Telemetry for prerendered tab.
 */
function trackPrerenderedPageTelemetry(pageRevealTimeStamp: DOMHighResTimeStamp): void {
    if (isPrerenderVisible()) {
        // There was a race condition within trackInfo which would undo the tmpl
        // This race condition is fixed by taking dependency on c.trackExtComplete
        Telemetry.addOrUpdateTmplProperty("isbackgroundpreload", "1");
        Telemetry.addOrUpdateTmplProperty("isprerender", "1");
        Telemetry.addOrUpdateCustomProperty(prerenderedPageAge, (pageRevealTimeStamp - performance.timing.navigationStart).toString());
    }
}

/**
 * Returns modified current URL with removed prerender query string.
 */
function getReloadUrl(): string {
    // background prerender query string is added by browser whenever the page is rendered in the background.
    // Since we are going to reload the page, we need to remove this query string to make sure the MSN servers
    // do not think of this request as a prerendered page.
    const url = location.href;

    return url ? url.replace(/[?&]prerender(=\d)?/, "") : "";
}

/**
 * fire pageReveal telemetry
 * @param pageRevealTimeStamp - pageReveal start timestamp
 */
function firePageRevealTelemetry(pageRevealTimeStamp: DOMHighResTimeStamp): void {
    trackPrerenderedPageTelemetry(pageRevealTimeStamp);
    trackPrerenderedPageReloadTelemetry();
}

/**
 * fail safe measure to remove overlay after timeout.
 */
function removeOverlayWithTimeout(): void {
    // fail safe measure to remove overlay after timeout.
    overlayTimeout = setTimeout(() => {
        removePrerenderOverlay();
    }, 1000);
}